import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FfrHost, FfrServer } from '@/types/ffr';

interface Props {
  servers: FfrServer[];
  activeHost: FfrHost;
  onSelect(host: FfrHost): void;
}

/**
 * FFR analogue of SportsSourceMenu — lists only the servers present on the
 * title (ok.ru / Dailymotion / filemoon), pre-ordered by the backend
 * (okru → dailymotion → filemoon). A concrete server is always active, so
 * there is no "Auto" row and no preflight probe (every server is a playable
 * iframe with nothing to resolve).
 */
export function FfrServerMenu({ servers, activeHost, onSelect }: Props): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const active = servers.find((s) => s.host === activeHost);

  return (
    <div className="source-menu ffr-server-menu">
      <button
        type="button"
        className="btn-ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t('source.trigger', { name: active?.label ?? activeHost })}
      </button>
      {open && (
        <ul role="menu" className="source-menu__list">
          {servers.map((server) => (
            <li key={server.host} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={server.host === activeHost}
                className={`source-menu__row${server.host === activeHost ? ' is-active' : ''}`}
                onClick={() => { onSelect(server.host); setOpen(false); }}
              >
                <span>{server.label}</span>
                <span className="ffr-server-menu__parts">
                  {t('ffr.partsCount', { count: server.parts.length })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
